import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth';
import { RequireAuth, GuestOnly, RequireRole } from './components/Guards';
import { DashboardLayout } from './components/DashboardLayout';
import { AuthLayout } from './components/AuthLayout';
import { AdminSecretGate } from './components/AdminSecretGate';
import { Spinner } from './components/ui/Spinner';

// ── Lazy-loaded pages (code-split per route) ────────────────────
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const OverviewPage = lazy(() => import('./pages/OverviewPage').then(m => ({ default: m.OverviewPage })));
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage').then(m => ({ default: m.ApiKeysPage })));
const TokensPage = lazy(() => import('./pages/TokensPage').then(m => ({ default: m.TokensPage })));
const TokenDeployPage = lazy(() => import('./pages/TokenDeployPage').then(m => ({ default: m.TokenDeployPage })));
const TokenDetailPage = lazy(() => import('./pages/TokenDetailPage').then(m => ({ default: m.TokenDetailPage })));
const TransactionsPage = lazy(() => import('./pages/TransactionsPage').then(m => ({ default: m.TransactionsPage })));
const TransactionDetailPage = lazy(() => import('./pages/TransactionDetailPage').then(m => ({ default: m.TransactionDetailPage })));
const NetworksPage = lazy(() => import('./pages/NetworksPage').then(m => ({ default: m.NetworksPage })));
const WebhooksPage = lazy(() => import('./pages/WebhooksCrudPage').then(m => ({ default: m.WebhooksPage })));
const GasPage = lazy(() => import('./pages/GasPage').then(m => ({ default: m.GasPage })));
const EventsPage = lazy(() => import('./pages/EventsPage').then(m => ({ default: m.EventsPage })));
const EventDetailPage = lazy(() => import('./pages/EventDetailPage').then(m => ({ default: m.EventDetailPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const VestingPage = lazy(() => import('./pages/VestingPage').then(m => ({ default: m.VestingPage })));
const VestingDeployPage = lazy(() => import('./pages/VestingDeployPage').then(m => ({ default: m.VestingDeployPage })));
const VestingDetailPage = lazy(() => import('./pages/VestingDetailPage').then(m => ({ default: m.VestingDetailPage })));
const VaultsPage = lazy(() => import('./pages/VaultsPage').then(m => ({ default: m.VaultsPage })));
const VaultDeployPage = lazy(() => import('./pages/VaultDeployPage').then(m => ({ default: m.VaultDeployPage })));
const VaultDetailPage = lazy(() => import('./pages/VaultDetailPage').then(m => ({ default: m.VaultDetailPage })));
const GovernancePage = lazy(() => import('./pages/GovernancePage').then(m => ({ default: m.GovernancePage })));
const GovernanceDeployPage = lazy(() => import('./pages/GovernanceDeployPage').then(m => ({ default: m.GovernanceDeployPage })));
const GovernanceDetailPage = lazy(() => import('./pages/GovernanceDetailPage').then(m => ({ default: m.GovernanceDetailPage })));
const ProposalDetailPage = lazy(() => import('./pages/ProposalDetailPage').then(m => ({ default: m.ProposalDetailPage })));
const MultisigPage = lazy(() => import('./pages/MultisigPage').then(m => ({ default: m.MultisigPage })));
const MultisigDeployPage = lazy(() => import('./pages/MultisigDeployPage').then(m => ({ default: m.MultisigDeployPage })));
const MultisigDetailPage = lazy(() => import('./pages/MultisigDetailPage').then(m => ({ default: m.MultisigDetailPage })));
const TbaPage = lazy(() => import('./pages/TbaPage').then(m => ({ default: m.TbaPage })));
const TbaDetailPage = lazy(() => import('./pages/TbaDetailPage').then(m => ({ default: m.TbaDetailPage })));
const PriceFeedsPage = lazy(() => import('./pages/PriceFeedsPage').then(m => ({ default: m.PriceFeedsPage })));
const VrfPage = lazy(() => import('./pages/VrfPage').then(m => ({ default: m.VrfPage })));
const BillingPage = lazy(() => import('./pages/BillingPage').then(m => ({ default: m.BillingPage })));
const AdminBillingPage = lazy(() => import('./pages/AdminBillingPage').then(m => ({ default: m.AdminBillingPage })));
const AdminOverviewPage = lazy(() => import('./pages/AdminOverviewPage').then(m => ({ default: m.AdminOverviewPage })));
const AdminTenantsPage = lazy(() => import('./pages/AdminTenantsPage').then(m => ({ default: m.AdminTenantsPage })));
const AdminTenantDetailPage = lazy(() => import('./pages/AdminTenantDetailPage').then(m => ({ default: m.AdminTenantDetailPage })));
const AdminPaymentsPage = lazy(() => import('./pages/AdminPaymentsPage').then(m => ({ default: m.AdminPaymentsPage })));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage').then(m => ({ default: m.AdminUsersPage })));
const AdminLeadsPage = lazy(() => import('./pages/AdminLeadsPage').then(m => ({ default: m.AdminLeadsPage })));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const ConnectPage = lazy(() => import('./pages/ConnectPage').then(m => ({ default: m.ConnectPage })));
const ConnectAccountDetailPage = lazy(() => import('./pages/ConnectAccountDetailPage').then(m => ({ default: m.ConnectAccountDetailPage })));
const IdentitiesPage = lazy(() => import('./pages/IdentitiesPage').then(m => ({ default: m.IdentitiesPage })));
const IdentityDetailPage = lazy(() => import('./pages/IdentityDetailPage').then(m => ({ default: m.IdentityDetailPage })));
const CompliancePage = lazy(() => import('./pages/CompliancePage').then(m => ({ default: m.CompliancePage })));
const TrustedIssuersPage = lazy(() => import('./pages/TrustedIssuersPage').then(m => ({ default: m.TrustedIssuersPage })));
const ClaimTopicsPage = lazy(() => import('./pages/ClaimTopicsPage').then(m => ({ default: m.ClaimTopicsPage })));

export function App() {
  const initialize = useAuthStore((s) => s.initialize);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  return (
    <Suspense fallback={<Spinner />}>
    <Routes>
      {/* ── Auth routes (guest only) ──────────── */}
      <Route
        element={
          <GuestOnly>
            <AuthLayout />
          </GuestOnly>
        }
      >
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
      </Route>

      {/* ── Dashboard routes (auth required) ──── */}
      <Route
        element={
          <RequireAuth>
            <DashboardLayout />
          </RequireAuth>
        }
      >
        <Route index element={<OverviewPage />} />
        <Route path="/api-keys" element={<ApiKeysPage />} />
        <Route path="/tokens" element={<TokensPage />} />
        <Route path="/tokens/deploy" element={<TokenDeployPage />} />
        <Route path="/tokens/:id" element={<TokenDetailPage />} />
        <Route path="/transactions" element={<TransactionsPage />} />
        <Route path="/transactions/:id" element={<TransactionDetailPage />} />
        <Route path="/wallets" element={<Navigate to="/connect" replace />} />
        <Route path="/wallets/:id" element={<Navigate to="/connect" replace />} />
        <Route path="/networks" element={<NetworksPage />} />
        <Route path="/webhooks" element={<WebhooksPage />} />
        <Route path="/gas" element={<GasPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/events/:id" element={<EventDetailPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/vesting" element={<VestingPage />} />
        <Route path="/vesting/deploy" element={<VestingDeployPage />} />
        <Route path="/vesting/:id" element={<VestingDetailPage />} />
        <Route path="/vaults" element={<VaultsPage />} />
        <Route path="/vaults/deploy" element={<VaultDeployPage />} />
        <Route path="/vaults/:id" element={<VaultDetailPage />} />
        <Route path="/governance" element={<GovernancePage />} />
        <Route path="/governance/deploy" element={<GovernanceDeployPage />} />
        <Route path="/governance/:id" element={<GovernanceDetailPage />} />
        <Route path="/governance/:id/proposals/:proposalId" element={<ProposalDetailPage />} />
        <Route path="/multisig" element={<MultisigPage />} />
        <Route path="/multisig/deploy" element={<MultisigDeployPage />} />
        <Route path="/multisig/:id" element={<MultisigDetailPage />} />
        <Route path="/tba" element={<TbaPage />} />
        <Route path="/tba/:id" element={<TbaDetailPage />} />
        <Route path="/price-feeds" element={<PriceFeedsPage />} />
        <Route path="/vrf" element={<VrfPage />} />
        <Route path="/connect" element={<ConnectPage />} />
        <Route path="/connect/:address" element={<ConnectAccountDetailPage />} />
        <Route path="/identities" element={<IdentitiesPage />} />
        <Route path="/identities/:id" element={<IdentityDetailPage />} />
        <Route path="/compliance" element={<CompliancePage />} />
        <Route path="/trusted-issuers" element={<TrustedIssuersPage />} />
        <Route path="/claim-topics" element={<ClaimTopicsPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/admin" element={<RequireRole roles={['admin']}><AdminSecretGate><AdminOverviewPage /></AdminSecretGate></RequireRole>} />
        <Route path="/admin/tenants" element={<RequireRole roles={['admin']}><AdminSecretGate><AdminTenantsPage /></AdminSecretGate></RequireRole>} />
        <Route path="/admin/tenants/:id" element={<RequireRole roles={['admin']}><AdminSecretGate><AdminTenantDetailPage /></AdminSecretGate></RequireRole>} />
        <Route path="/admin/payments" element={<RequireRole roles={['admin']}><AdminSecretGate><AdminPaymentsPage /></AdminSecretGate></RequireRole>} />
        <Route path="/admin/users" element={<RequireRole roles={['admin']}><AdminSecretGate><AdminUsersPage /></AdminSecretGate></RequireRole>} />
        <Route path="/admin/billing" element={<RequireRole roles={['admin']}><AdminBillingPage /></RequireRole>} />
        <Route path="/admin/leads" element={<RequireRole roles={['admin']}><AdminLeadsPage /></RequireRole>} />
        <Route path="/analytics" element={<AnalyticsPage />} />
      </Route>

      {/* ── Fallback ──────────────────────────── */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}
